'use client';

import { useAnswerTemplates } from '@/components/answer-templates/use-answer-templates';
import { SettingsQuestionnaireModalView } from '@/components/settings-questionnaire-modal/SettingsQuestionnaireModalView';
import { SettingsTabId } from '@/components/settings-questionnaire-modal/settings-questionnaire.schema';
import { useSettingsQuestionnaireModalController } from '@/components/settings-questionnaire-modal/use-settings-questionnaire-modal-controller';
import { useSettingsMaxNotificationsModalController } from '@/components/settings-max-notifications-modal/use-settings-max-notifications-modal-controller';
import { useAuth } from '@/context/auth.context';
import { FC, useEffect, useRef, useState } from 'react';

type SettingsQuestionnaireModalProps = {
    isShown: boolean;
    setIsShown: (val: boolean) => void;
    initialTab?: SettingsTabId;
    openPaymentRequestKey?: number;
};

const SettingsQuestionnaireModal: FC<SettingsQuestionnaireModalProps> = ({
    isShown,
    setIsShown,
    initialTab = 'general',
    openPaymentRequestKey = 0,
}) => {
    const { accessToken } = useAuth();
    const [activeTab, setActiveTab] = useState<SettingsTabId>(initialTab);
    const handledPaymentRequestKeyRef = useRef(0);
    const controller = useSettingsQuestionnaireModalController({ isShown, setIsShown });
    const { hasLoadedSettings, openPaymentForm } = controller;
    const maxNotificationsController = useSettingsMaxNotificationsModalController({
        isShown: isShown && activeTab === 'notifications',
        setIsShown,
    });
    const answerTemplatesController = useAnswerTemplates({
        isShown: isShown && activeTab === 'answer-templates',
        accessToken,
        loadOnShow: true,
    });

    useEffect(() => {
        if (!isShown) {
            return;
        }

        setActiveTab(initialTab);
    }, [initialTab, isShown]);

    useEffect(() => {
        if (
            !isShown ||
            !openPaymentRequestKey ||
            handledPaymentRequestKeyRef.current === openPaymentRequestKey ||
            !hasLoadedSettings
        ) {
            return;
        }

        handledPaymentRequestKeyRef.current = openPaymentRequestKey;
        setActiveTab('limits-payments');
        openPaymentForm();
    }, [hasLoadedSettings, isShown, openPaymentRequestKey, openPaymentForm]);

    return (
        <SettingsQuestionnaireModalView
            isShown={isShown}
            setIsShown={setIsShown}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            maxNotifications={maxNotificationsController}
            answerTemplates={answerTemplatesController}
            {...controller}
        />
    );
};

export default SettingsQuestionnaireModal;
